/**
 * 長文の出題を画面に収めるための文字サイズ計算(docs/02_game_design.md §3)。
 *
 * 単語ステージだけだった頃は CSS の `clamp()` 固定サイズで足りていたが、
 * 文章ステージ(日本語 reading 最長35かな ≒ ローマ字70打鍵 / 英語 display 最長45文字)が
 * 入ったことで、Chromebook の 1366x768 でも 1行に収まらなくなった。
 *
 * ここでは「文字数と画面サイズから、何行に折り返すか」を見積もって
 * 段階的にフォントを縮める。描画に依存しない純関数なので単体テストできる。
 * 実測との誤差は `ui/components/FitText.tsx` の縮小ループが最後に吸収する。
 */

/** 画面サイズ(px)。 */
export interface Viewport {
  readonly width: number;
  readonly height: number;
}

export type FitRole = 'display' | 'ruby' | 'romaji' | 'meaning';

export interface RoleConfig {
  /** これ以上は小さくしない下限(小学生が読める最小)。 */
  readonly minPx: number;
  /** 画面幅に対する基準サイズ(従来の CSS clamp の中央値と同じ)。 */
  readonly vw: number;
  /** 上限(従来の CSS clamp の最大値と同じ)。 */
  readonly maxPx: number;
  /** 許容する行数。これを超えるならフォントを下げる。 */
  readonly maxLines: number;
  readonly lineHeight: number;
  /** 画面高に対する高さ予算。全役割の合計がプレイ画面の中段に収まるようにする。 */
  readonly heightRatio: number;
  /** letter-spacing(em)。幅の見積もりに効く。 */
  readonly letterSpacingEm: number;
  /** 等幅フォント(ローマ字ガイド)か。 */
  readonly mono: boolean;
}

/**
 * 役割ごとの設定。
 *
 * `vw`/`maxPx` は変更前の CSS(styles.css の `.word-display` など)と同じ値にしてある。
 * 短い単語では従来とまったく同じ見た目になり、長文のときだけ縮む。
 *
 * 高さ予算の合計は 0.22+0.08+0.18+0.09 = 0.57 → 768px 画面で約 437px。
 * HUD(約66px)とキーボード(約26vh)を引いた中段の実寸とほぼ同じ。
 */
const ROLE_CONFIG: Readonly<Record<FitRole, RoleConfig>> = {
  display: {
    minPx: 18,
    vw: 6.5,
    maxPx: 72,
    maxLines: 2,
    lineHeight: 1.15,
    heightRatio: 0.22,
    letterSpacingEm: 0,
    mono: false,
  },
  ruby: {
    minPx: 11,
    vw: 2,
    maxPx: 20,
    maxLines: 2,
    lineHeight: 1.3,
    heightRatio: 0.08,
    letterSpacingEm: 0.2,
    mono: false,
  },
  romaji: {
    minPx: 14,
    vw: 4,
    maxPx: 44,
    maxLines: 2,
    lineHeight: 1.25,
    heightRatio: 0.18,
    letterSpacingEm: 0.08,
    mono: true,
  },
  meaning: {
    minPx: 12,
    vw: 2.4,
    maxPx: 26,
    maxLines: 2,
    lineHeight: 1.3,
    heightRatio: 0.09,
    letterSpacingEm: 0,
    mono: false,
  },
};

/** 画面幅のうちテキストに使える割合(左右パディング + 安全マージン)。 */
const WIDTH_RATIO = 0.9;

/**
 * 合成ボールド(1ウェイトのフォントに font-weight:800 を当てている)ぶんの太り。
 * 見積もりを実測より少しだけ大きめにして、はみ出す側に倒れないようにする。
 */
const BOLD_FUDGE = 1.04;

/** 全角として扱う文字(かな・漢字・全角記号)。 */
const FULL_WIDTH = /[\u3000-ヿ㐀-䶿一-鿿豈-﫿＀-｠￠-￦]/;

/** 半角プロポーショナルの字幅(em)。ざっくりで良いが、細い字を過小評価しない。 */
function latinWidthEm(ch: string): number {
  if (ch === ' ') return 0.28;
  if (/[iljI.,'!:;|]/.test(ch)) return 0.32;
  if (/[mwMW]/.test(ch)) return 0.88;
  if (/[A-Z]/.test(ch)) return 0.68;
  if (/[0-9]/.test(ch)) return 0.58;
  return 0.56;
}

/** 1文字ぶんの字幅(em)。letter-spacing を含む。 */
export function charWidthEm(
  ch: string,
  config: Pick<RoleConfig, 'mono' | 'letterSpacingEm'>,
): number {
  const base = config.mono ? 0.6 : FULL_WIDTH.test(ch) ? 1 : latinWidthEm(ch) * BOLD_FUDGE;
  return base + config.letterSpacingEm;
}

/** 行分割の単位。`spaceBefore` は行頭に来たら消えるスペース幅。 */
interface Token {
  readonly width: number;
  readonly spaceBefore: number;
}

/**
 * テキストを折返し単位に分ける。
 *
 * - 全角文字・等幅ガイドは1文字ずつ(どこでも折り返せる)
 * - 半角の語はスペース区切りの単語ごと(単語の途中では折り返さない)
 *   ただし1行に入りきらない長い語は `overflow-wrap: anywhere` で途中折返しになる
 */
export function tokenize(text: string, role: FitRole): readonly Token[] {
  const config = ROLE_CONFIG[role];
  const tokens: Token[] = [];
  let pendingSpace = 0;
  let wordWidth = 0;

  const flushWord = (): void => {
    if (wordWidth === 0) return;
    tokens.push({ width: wordWidth, spaceBefore: pendingSpace });
    pendingSpace = 0;
    wordWidth = 0;
  };

  for (const ch of text) {
    if (ch === ' ' || ch === '　') {
      flushWord();
      pendingSpace += charWidthEm(ch, config);
      continue;
    }
    const width = charWidthEm(ch, config);
    if (config.mono || FULL_WIDTH.test(ch)) {
      flushWord();
      tokens.push({ width, spaceBefore: pendingSpace });
      pendingSpace = 0;
      continue;
    }
    wordWidth += width;
  }
  flushWord();
  return tokens;
}

/**
 * 貪欲法で行数を数える(ブラウザの折返しと同じ順序)。
 * 1行に入らない語は `overflow-wrap: anywhere` 相当で途中折返しする。
 */
export function countLines(tokens: readonly Token[], availableEm: number): number {
  if (tokens.length === 0) return 1;
  if (availableEm <= 0) return Number.POSITIVE_INFINITY;
  let lines = 1;
  let used = 0;
  for (const token of tokens) {
    const space = used === 0 ? 0 : token.spaceBefore;
    if (used + space + token.width <= availableEm) {
      used += space + token.width;
      continue;
    }
    // 行を変える。
    if (token.width <= availableEm) {
      lines += 1;
      used = token.width;
      continue;
    }
    // 語そのものが1行より長い → 途中で折り返す。
    if (used > 0) lines += 1;
    const extra = Math.ceil(token.width / availableEm) - 1;
    lines += extra;
    used = token.width - extra * availableEm;
  }
  return lines;
}

/** その画面幅での上限サイズ(従来の CSS clamp と同じ値)。 */
export function maxFontSize(role: FitRole, viewport: Viewport): number {
  const { minPx, vw, maxPx } = ROLE_CONFIG[role];
  return Math.min(maxPx, Math.max(minPx, (viewport.width * vw) / 100));
}

/** テキストの高さ予算(px)。これを超えたぶんは CSS 側でクリップする。 */
export function maxHeightPx(role: FitRole, viewport: Viewport): number {
  return Math.round(viewport.height * ROLE_CONFIG[role].heightRatio);
}

/** その役割の行の高さ(px)。見積もり検算とテスト用。 */
export function lineHeightPx(role: FitRole, fontSize: number): number {
  return fontSize * ROLE_CONFIG[role].lineHeight;
}

export interface FitResult {
  /** 適用するフォントサイズ(px)。 */
  readonly fontSize: number;
  /** その大きさで見積もった行数。 */
  readonly lines: number;
  /** 縮小ループの下限(px)。 */
  readonly minFontSize: number;
  /** 要素に与える max-height(px)。 */
  readonly maxHeight: number;
}

/**
 * 文字列と画面サイズから、収まる最大のフォントサイズを選ぶ。
 *
 * 1px ずつ下げながら「許容行数以内 かつ 高さ予算以内」を満たす最大値を探す。
 * 最大 60 回程度のループで、出題が変わったときにしか走らない。
 */
export function fitText(role: FitRole, text: string, viewport: Viewport): FitResult {
  const config = ROLE_CONFIG[role];
  const tokens = tokenize(text, role);
  const available = viewport.width * WIDTH_RATIO;
  const heightBudget = viewport.height * config.heightRatio;
  const start = Math.floor(maxFontSize(role, viewport));

  for (let size = start; size >= config.minPx; size -= 1) {
    const lines = countLines(tokens, available / size);
    if (lines > config.maxLines) continue;
    if (lines * size * config.lineHeight > heightBudget) continue;
    return {
      fontSize: size,
      lines,
      minFontSize: config.minPx,
      maxHeight: Math.round(heightBudget),
    };
  }
  return {
    fontSize: config.minPx,
    lines: countLines(tokens, available / config.minPx),
    minFontSize: config.minPx,
    maxHeight: Math.round(heightBudget),
  };
}
